import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChartLineUp, MagnifyingGlass } from "@phosphor-icons/react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PricingPanel from "@/components/PricingPanel";

const CONDITIONS = ["Excellent", "Very good", "Good", "Fair", "Restored", "As-is"];
const inputCls = "w-full h-11 px-3 rounded-md border border-[#D6D3CD] bg-white text-base focus:outline-none focus:border-[#0D1B2A]";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [itemType, setItemType] = useState("");
  const [brand, setBrand] = useState("");
  const [era, setEra] = useState("");
  const [condition, setCondition] = useState("");
  const [category, setCategory] = useState("");

  // Committed query — panel only refetches when the seller runs the lookup.
  const [query, setQuery] = useState(null);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    api.get("/listings").then(({ data }) => setListings(Array.isArray(data) ? data : (data.items || []))).catch(() => {});
  }, []);

  const run = () => setQuery({ itemType, brandDesigner: brand, periodEra: era, condition, itemCategory: category });

  const pickListing = (id) => {
    const l = listings.find((x) => x.id === id);
    if (!l) return;
    const it = l.item_type || l.listing_headline || l.title || "";
    setItemType(it);
    setBrand(l.brand_designer || "");
    setEra(l.period_era || "");
    setCondition(l.condition || "");
    setCategory(l.item_category || "");
    setQuery({
      itemType: it, brandDesigner: l.brand_designer || "", periodEra: l.period_era || "",
      condition: l.condition || "", itemCategory: l.item_category || "",
    });
  };

  const listingOpts = useMemo(
    () => listings.slice(0, 200).map((l) => ({ id: l.id, label: (l.listing_headline || l.title || "Untitled").slice(0, 60) })),
    [listings]
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="pricing-page">
      <header className="sticky top-0 z-30 border-b border-[#D8D3C8] bg-[#FDFBF7]/90 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vv_logo.jpg" alt="Vintage Vantage" onClick={() => navigate("/dashboard")} className="h-9 w-9 rounded-full object-cover ring-1 ring-[#E5E0D6] cursor-pointer shrink-0 hover:ring-[#B47A3C] transition-all" data-testid="app-compass-mark" title="Vintage Vantage — home" />
            <button onClick={() => navigate("/dashboard")} data-testid="pricing-back-button" className="inline-flex items-center gap-2 font-medium hover:text-[#B47A3C] transition-colors">
              <ArrowLeft size={18} weight="bold" /> Dashboard
            </button>
          </div>
          <span className="text-sm font-semibold text-[#525252] truncate max-w-[200px]">{user?.name || user?.email}</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-2">
          <ChartLineUp size={22} weight="fill" className="text-[#B47A3C]" />
          <span className="label-caps">Marketplace intelligence · Pricing</span>
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">
          What's it worth?
        </h1>
        <p className="text-lg text-[#525252] leading-relaxed max-w-2xl mb-8">
          Describe a piece and get an evidence-based price range from real recent comparable sales — plus a
          strategy slider to price for a quick sale or a maximum return. This is guidance to help you decide, never an appraisal.
        </p>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Lookup form */}
          <div className="border border-[#0D1B2A] bg-white rounded-xl p-5 sm:p-6" data-testid="pricing-lookup-form">
            <h2 className="text-base font-bold mb-4">Describe the piece</h2>

            {listingOpts.length > 0 ? (
              <div className="mb-4">
                <label className="label-caps block mb-1.5">Start from one of your pieces</label>
                <Select onValueChange={pickListing}>
                  <SelectTrigger className="h-11 text-base" data-testid="pricing-listing-select">
                    <SelectValue placeholder="Pick a listing to prefill…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {listingOpts.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-4">
              <div>
                <label className="label-caps block mb-1.5">What is it? *</label>
                <input className={inputCls} value={itemType} onChange={(e) => setItemType(e.target.value)}
                  placeholder="e.g. walnut credenza, lounge chair" data-testid="pricing-input-itemtype" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">Brand / Designer</label>
                <input className={inputCls} value={brand} onChange={(e) => setBrand(e.target.value)}
                  placeholder="e.g. Milo Baughman" data-testid="pricing-input-brand" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-caps block mb-1.5">Era</label>
                  <input className={inputCls} value={era} onChange={(e) => setEra(e.target.value)}
                    placeholder="e.g. Mid-Century" data-testid="pricing-input-era" />
                </div>
                <div>
                  <label className="label-caps block mb-1.5">Condition</label>
                  <Select value={condition} onValueChange={setCondition}>
                    <SelectTrigger className="h-11 text-base" data-testid="pricing-input-condition">
                      <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <button type="button" onClick={run} disabled={!itemType.trim()}
                data-testid="pricing-run-button"
                className="w-full h-12 bg-[#0D1B2A] text-[#FDFBF7] rounded-md font-semibold inline-flex items-center justify-center gap-2 hover:bg-black transition-transform active:scale-[0.99] disabled:opacity-50">
                <MagnifyingGlass size={18} weight="bold" /> Get pricing
              </button>
            </div>
          </div>

          {/* Panel */}
          <div>
            {query ? (
              <PricingPanel
                itemType={query.itemType} brandDesigner={query.brandDesigner} periodEra={query.periodEra}
                itemCategory={query.itemCategory} condition={query.condition} variant="page"
              />
            ) : (
              <div className="border border-dashed border-[#D6D3CD] rounded-xl p-8 text-center bg-[#FBFAF6]" data-testid="pricing-empty-state">
                <ChartLineUp size={32} weight="light" className="mx-auto text-[#B8B2A6] mb-3" />
                <p className="text-[#8A867D]">Describe a piece and run the lookup to see its recommended range.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
